/**
 * Description: Velocity-based physics; reads limits from character config; uses environment for collisions.
 * Usage: Construct with (environment, host); init(config); update(dtSec) each frame.
 */
export class MovementEngine {
  constructor(environment, host) {
    this._env = environment;
    this._host = host;
    this._config = null;
    this._x = 0;
    this._y = 0;
    this._vx = 0;
    this._vy = 0;
    this._grounded = false;
    this._currentPlatform = null;
    this._facingDir = 1;
    this._walkDir = 0;
    this._speedMult = 1;
    this._paused = false;
    this._PET_SIZE = 48;
    this._GRAVITY = 900;
    this._WALK_SPEED = 70;
    this._WALK_ACCEL = 400;
    this._JUMP_FORCE = 320;
    this._FRICTION = 8;
  }

  _clamp(val, min, max) {
    return Math.max(min, Math.min(max, val));
  }

  init(cfg) {
    this._config = cfg;
    this._PET_SIZE = cfg.size || 48;

    const lim = cfg.limits || cfg.physics || {};
    this._GRAVITY = lim.gravity || 900;
    this._WALK_SPEED = lim.walkSpeed || 70;
    this._WALK_ACCEL = lim.walkAccel || 400;
    this._JUMP_FORCE = lim.jumpForce || 320;
    this._FRICTION = lim.friction || 8;

    const speedMod = 0.3 + ((cfg.movementSpeed - 1) / 99) * 1.7;
    this._WALK_SPEED *= speedMod;
    this._WALK_ACCEL *= speedMod;

    const win = this._host.window;
    this._x = win.innerWidth * 0.3 + Math.random() * win.innerWidth * 0.4;
    this._y = win.innerHeight - this._PET_SIZE;
    this._grounded = true;

    if (cfg.persistenceEnabled && win.localStorage) {
      try {
        const saved = JSON.parse(win.localStorage.getItem('ndp_state'));
        if (saved && typeof saved.x === 'number') {
          this._x = this._clamp(saved.x, 0, win.innerWidth - this._PET_SIZE);
          this._y = this._clamp(saved.y, 0, win.innerHeight - this._PET_SIZE);
        }
      } catch {
        /* ignore */
      }
    }
  }

  update(dtSec) {
    if (this._paused) return;

    if (!this._grounded) {
      this._vy += this._GRAVITY * dtSec;
    }

    if (this._walkDir !== 0 && this._grounded) {
      const targetVx = this._WALK_SPEED * this._speedMult * this._walkDir;
      this._vx += (targetVx - this._vx) * Math.min(1, this._WALK_ACCEL * dtSec);
    }

    if (this._grounded && this._walkDir === 0) {
      if (Math.abs(this._vx) > 0.5) {
        this._vx *= Math.max(0, 1 - this._FRICTION * dtSec);
      } else {
        this._vx = 0;
      }
    }

    this._x += this._vx * dtSec;
    this._y += this._vy * dtSec;

    const win = this._host.window;
    const vw = win.innerWidth;
    if (this._x < 0) {
      this._x = 0;
      this._vx = 0;
    }
    if (this._x > vw - this._PET_SIZE) {
      this._x = vw - this._PET_SIZE;
      this._vx = 0;
    }

    this._resolveCollisions();

    if (this._vx > 0.5) this._facingDir = 1;
    else if (this._vx < -0.5) this._facingDir = -1;
  }

  _resolveCollisions() {
    const petBottom = this._y + this._PET_SIZE;
    const petCenterX = this._x + this._PET_SIZE / 2;
    const platforms = this._env.getPlatforms();

    this._grounded = false;
    this._currentPlatform = null;

    for (let i = 0; i < platforms.length; i++) {
      const p = platforms[i];
      if (petCenterX < p.left || petCenterX > p.right) continue;
      if (p.y < this._y) continue;

      if (petBottom >= p.y - 2 && petBottom <= p.y + 20 && this._vy >= 0) {
        this._y = p.y - this._PET_SIZE;
        this._vy = 0;
        this._grounded = true;
        this._currentPlatform = p;
        break;
      }
    }

    if (!this._grounded) {
      const win = this._host.window;
      const floorY = win.innerHeight;
      if (petBottom >= floorY) {
        this._y = floorY - this._PET_SIZE;
        this._vy = 0;
        this._grounded = true;
        this._currentPlatform = { left: 0, right: win.innerWidth, y: floorY };
      }
    }
  }

  walkTo(direction) {
    this._walkDir = direction > 0 ? 1 : direction < 0 ? -1 : 0;
    if (this._walkDir !== 0) this._facingDir = this._walkDir;
  }

  stopWalk() {
    this._walkDir = 0;
  }

  setSpeedMultiplier(mult) {
    this._speedMult = Math.max(0.1, Math.min(4, mult));
  }

  jump() {
    if (!this._grounded) return;
    this._vy = -this._JUMP_FORCE;
    this._grounded = false;
    this._currentPlatform = null;
  }

  isGrounded() {
    return this._grounded;
  }

  isAtEdge() {
    if (!this._currentPlatform) return false;
    const petCenterX = this._x + this._PET_SIZE / 2;
    const margin = this._PET_SIZE * 0.4;
    return petCenterX - margin < this._currentPlatform.left || petCenterX + margin > this._currentPlatform.right;
  }

  isAtViewportEdge() {
    const win = this._host.window;
    return this._x <= 3 || this._x >= win.innerWidth - this._PET_SIZE - 3;
  }

  getPosition() {
    return { x: this._x, y: this._y };
  }

  getVelocity() {
    return { x: this._vx, y: this._vy };
  }

  getFacing() {
    return this._facingDir;
  }

  getCurrentPlatform() {
    return this._currentPlatform;
  }

  pause() {
    this._paused = true;
  }

  resume() {
    this._paused = false;
  }

  isPaused() {
    return this._paused;
  }
}
